const DECIMAL_PATTERN = /^([+-]?)(?:(\d+)(?:\.(\d*))?|\.(\d+))(?:[eE]([+-]?\d+))?$/;

function normalizeDecimal(value) {
    if (value === null || typeof value === 'undefined' || value === '') {
        return null;
    }

    const input = String(value).trim().replace(/,/g, '');
    const match = input.match(DECIMAL_PATTERN);
    if (!match) {
        return null;
    }

    const sign = match[1] === '-' ? '-' : '';
    const integer = typeof match[2] === 'string' ? match[2] : '0';
    const fraction = typeof match[2] === 'string' ? (match[3] || '') : (match[4] || '');
    const exponent = match[5] ? parseInt(match[5], 10) : 0;
    const digits = integer + fraction;
    const decimalPosition = integer.length + exponent;

    let plain;
    if (decimalPosition <= 0) {
        plain = `0.${'0'.repeat(-decimalPosition)}${digits}`;
    } else if (decimalPosition >= digits.length) {
        plain = digits + '0'.repeat(decimalPosition - digits.length);
    } else {
        plain = `${digits.slice(0, decimalPosition)}.${digits.slice(decimalPosition)}`;
    }

    let [whole, decimal = ''] = plain.split('.');
    whole = whole.replace(/^0+(?=\d)/, '') || '0';
    decimal = decimal.replace(/0+$/, '');

    if (whole === '0' && decimal === '') {
        return '0';
    }

    return `${sign}${whole}${decimal ? `.${decimal}` : ''}`;
}

function parseDecimal(value) {
    const normalized = normalizeDecimal(value) || '0';
    const negative = normalized.startsWith('-');
    const unsigned = negative ? normalized.slice(1) : normalized;
    const [whole, fraction = ''] = unsigned.split('.');
    const digits = (whole + fraction).replace(/^0+/, '') || '0';

    return {
        sign: digits === '0' ? 1 : (negative ? -1 : 1),
        digits,
        scale: fraction.length,
    };
}

function trimUnsigned(value) {
    return value.replace(/^0+/, '') || '0';
}

function compareUnsigned(left, right) {
    const a = trimUnsigned(left);
    const b = trimUnsigned(right);
    if (a.length !== b.length) {
        return a.length > b.length ? 1 : -1;
    }
    if (a === b) {
        return 0;
    }
    return a > b ? 1 : -1;
}

function addUnsigned(left, right) {
    let carry = 0;
    let result = '';
    let i = left.length - 1;
    let j = right.length - 1;

    while (i >= 0 || j >= 0 || carry) {
        const sum = (i >= 0 ? left.charCodeAt(i--) - 48 : 0)
            + (j >= 0 ? right.charCodeAt(j--) - 48 : 0)
            + carry;
        result = String(sum % 10) + result;
        carry = Math.floor(sum / 10);
    }

    return trimUnsigned(result);
}

function subtractUnsigned(left, right) {
    let borrow = 0;
    let result = '';
    let i = left.length - 1;
    let j = right.length - 1;

    while (i >= 0) {
        let value = left.charCodeAt(i--) - 48 - borrow;
        const subtract = j >= 0 ? right.charCodeAt(j--) - 48 : 0;
        if (value < subtract) {
            value += 10;
            borrow = 1;
        } else {
            borrow = 0;
        }
        result = String(value - subtract) + result;
    }

    return trimUnsigned(result);
}

function multiplyUnsigned(left, right) {
    const a = trimUnsigned(left);
    const b = trimUnsigned(right);
    if (a === '0' || b === '0') {
        return '0';
    }

    const result = new Array(a.length + b.length).fill(0);
    for (let i = a.length - 1; i >= 0; i -= 1) {
        for (let j = b.length - 1; j >= 0; j -= 1) {
            const position = i + j + 1;
            const product = (a.charCodeAt(i) - 48) * (b.charCodeAt(j) - 48)
                + result[position];
            result[position] = product % 10;
            result[position - 1] += Math.floor(product / 10);
        }
    }

    return trimUnsigned(result.join(''));
}

function incrementUnsigned(value) {
    return addUnsigned(value, '1');
}

function composeDecimal(sign, digits, scale) {
    const unsigned = trimUnsigned(digits);
    if (unsigned === '0') {
        return '0';
    }

    const padded = unsigned.padStart(scale + 1, '0');
    const splitAt = padded.length - scale;
    const whole = padded.slice(0, splitAt).replace(/^0+(?=\d)/, '') || '0';
    const fraction = scale > 0 ? padded.slice(splitAt).replace(/0+$/, '') : '';

    return `${sign < 0 ? '-' : ''}${whole}${fraction ? `.${fraction}` : ''}`;
}

export function addDecimals(left, right) {
    const a = parseDecimal(left);
    const b = parseDecimal(right);
    const scale = Math.max(a.scale, b.scale);
    const aDigits = a.digits + '0'.repeat(scale - a.scale);
    const bDigits = b.digits + '0'.repeat(scale - b.scale);

    if (a.sign === b.sign) {
        return composeDecimal(a.sign, addUnsigned(aDigits, bDigits), scale);
    }

    const comparison = compareUnsigned(aDigits, bDigits);
    if (comparison === 0) {
        return '0';
    }

    return comparison > 0
        ? composeDecimal(a.sign, subtractUnsigned(aDigits, bDigits), scale)
        : composeDecimal(b.sign, subtractUnsigned(bDigits, aDigits), scale);
}

export function multiplyDecimals(left, right) {
    const a = parseDecimal(left);
    const b = parseDecimal(right);
    return composeDecimal(
        a.sign * b.sign,
        multiplyUnsigned(a.digits, b.digits),
        a.scale + b.scale
    );
}

export function isPositiveDecimal(value) {
    const normalized = normalizeDecimal(value);
    return normalized !== null && !normalized.startsWith('-') && /[1-9]/.test(normalized);
}

export function formatDecimal(value, decimalPlaces = 2) {
    const places = Math.max(0, Math.trunc(Number(decimalPlaces) || 0));
    const parsed = parseDecimal(value);
    let digits = parsed.digits;
    let scale = parsed.scale;

    if (scale > places) {
        const working = digits.padStart(scale + 1, '0');
        const cutCount = scale - places;
        const keepLength = working.length - cutCount;
        const roundDigit = working.charCodeAt(keepLength) - 48;
        digits = trimUnsigned(working.slice(0, keepLength));
        if (roundDigit >= 5) {
            digits = incrementUnsigned(digits);
        }
        scale = places;
    } else if (scale < places) {
        digits += '0'.repeat(places - scale);
        scale = places;
    }

    const fixed = trimUnsigned(digits).padStart(scale + 1, '0');
    const splitAt = fixed.length - scale;
    let whole = scale > 0 ? fixed.slice(0, splitAt) : fixed;
    const fraction = scale > 0 ? fixed.slice(splitAt).padEnd(scale, '0') : '';
    whole = whole.replace(/^0+(?=\d)/, '') || '0';
    whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    const isZero = !/[1-9]/.test(fixed);
    const sign = parsed.sign < 0 && !isZero ? '-' : '';
    return `${sign}${whole}${scale > 0 ? `.${fraction}` : ''}`;
}

export function formatFiat(value, rate, decimalPlaces = 2) {
    return formatDecimal(multiplyDecimals(value, rate), decimalPlaces);
}

export function formatFiatAdaptive(value, rate, minimumPlaces = 2, maximumPlaces = 12) {
    const normalized = normalizeDecimal(value) || '0';
    const unsigned = normalized.startsWith('-') ? normalized.slice(1) : normalized;
    const fraction = unsigned.split('.')[1] || '';
    const min = Math.max(0, Math.trunc(Number(minimumPlaces) || 0));
    const max = Math.max(min, Math.trunc(Number(maximumPlaces) || min));
    const places = Math.min(max, Math.max(min, fraction.length + 1));
    return formatFiat(value, rate, places);
}

export function formatFiatProduct(values, rate, decimalPlaces = 2) {
    const factors = Array.isArray(values) ? values : [values];
    const product = factors.reduce(
        (result, value) => multiplyDecimals(result, value),
        '1'
    );
    return formatDecimal(multiplyDecimals(product, rate), decimalPlaces);
}

export function formatFiatSum(values, rate, decimalPlaces = 2) {
    const terms = Array.isArray(values) ? values : [values];
    const sum = terms.reduce(
        (result, value) => addDecimals(result, value),
        '0'
    );
    return formatDecimal(multiplyDecimals(sum, rate), decimalPlaces);
}
